import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  FileText, Settings, Download, MoreVertical, TrendingUp, Award, Info,
  Check, X, ChevronDown, ChevronUp, Users
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_PREFORMATION_QUESTIONS = [
  "Cette formation nécessite un certain niveau de pratique des outils numériques: auriez-vous des difficultés avec l'un des outils ou applications suivantes",
  "Etes-vous dans une situation de handicap reconnue officiellement?",
  "Si oui, pourriez-vous préciser cet handicap afin de permettre sa possible prise en compte?",
  "Seriez-vous dans une situation de mobilité réduite pendant la durée de la formation?",
  "Seriez vous dans une autre situation gênante dont la non-prise en compte pourrait handicaper votre participation à cette formation?",
];

const ALL_CHAUD_QUESTIONS = [
  "Préparation de la formation",
  "Organisation de la formation",
  "Déroulement de la formation",
  "Le rythme de la formation était il ?",
  "Contenu de la formation",
  "À ce jour, considérez-vous maîtriser les objectifs du programme ?",
  "Évaluez vos compétences",
  "Efficacité de la formation",
  "Recommanderiez vous cette formation ?",
  "Quels sont les points forts de cette formation ?",
  "Quels sont les points faibles de cette formation ?",
  "Note générale de la formation ?",
  "Autres remarques",
];

const SuiviQualite = ({ sessionId, sessionTitle = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('client');
  const [showParamsModal, setShowParamsModal] = useState(false);

  // Config state
  const [anonymize, setAnonymize] = useState(false);
  const [showProgressionCurve, setShowProgressionCurve] = useState(true);
  const [visiblePreformation, setVisiblePreformation] = useState([]);
  const [visibleChaud, setVisibleChaud] = useState([...ALL_CHAUD_QUESTIONS]);
  const [synthese, setSynthese] = useState('');

  const fetchData = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/suivi-qualite`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        if (d.config) {
          setAnonymize(d.config.anonymize || false);
          setShowProgressionCurve(d.config.show_progression_curve !== false);
          setVisiblePreformation(d.config.visible_preformation || []);
          setVisibleChaud(d.config.visible_chaud || [...ALL_CHAUD_QUESTIONS]);
          setSynthese(d.config.synthese || '');
        }
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [sessionId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveConfig = async () => {
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/suivi-qualite/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          anonymize,
          show_progression_curve: showProgressionCurve,
          visible_preformation: visiblePreformation,
          visible_chaud: visibleChaud,
          synthese,
        }),
      });
      if (res.ok) {
        toast.success('Configuration enregistrée');
        setShowParamsModal(false);
      }
    } catch (e) { toast.error('Erreur'); }
  };

  const handleSaveSynthese = async () => {
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/suivi-qualite/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          anonymize, show_progression_curve: showProgressionCurve,
          visible_preformation: visiblePreformation,
          visible_chaud: visibleChaud,
          synthese,
        }),
      });
      if (res.ok) toast.success('Synthèse enregistrée');
    } catch (e) { toast.error('Erreur'); }
  };

  const togglePreformationQ = (q) => {
    setVisiblePreformation(prev =>
      prev.includes(q) ? prev.filter(x => x !== q) : [...prev, q]
    );
  };

  const toggleChaudQ = (q) => {
    setVisibleChaud(prev =>
      prev.includes(q) ? prev.filter(x => x !== q) : [...prev, q]
    );
  };

  const toggleAllPreformation = () => {
    if (visiblePreformation.length === ALL_PREFORMATION_QUESTIONS.length) {
      setVisiblePreformation([]);
    } else {
      setVisiblePreformation([...ALL_PREFORMATION_QUESTIONS]);
    }
  };

  const toggleAllChaud = () => {
    if (visibleChaud.length === ALL_CHAUD_QUESTIONS.length) {
      setVisibleChaud([]);
    } else {
      setVisibleChaud([...ALL_CHAUD_QUESTIONS]);
    }
  };

  const criteriaScores = data?.criteria_scores || {};
  const overallScore = data?.overall_score || 0;
  const preScore = data?.pre_score || 0;
  const postScore = data?.post_score || 0;
  const progression = data?.progression || 0;
  const objectifs = data?.objectifs_pedagogiques || [];

  if (loading) {
    return <div className="py-12 text-center text-sm text-slate-400">Chargement...</div>;
  }

  return (
    <div className="space-y-6 mt-4" data-testid="suivi-qualite-content">
      {/* Session title + sub-tabs */}
      <div>
        <h3 className="text-base font-bold text-slate-900 dark:text-white mb-3 italic">
          {sessionTitle || 'Session'}
        </h3>
        <div className="flex gap-2" data-testid="qualite-sub-tabs">
          <button
            className={`px-5 py-2 text-sm font-medium rounded-lg border transition-all ${
              subTab === 'client'
                ? 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white shadow-sm'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setSubTab('client')}
            data-testid="qualite-tab-client">
            Sélection du client
          </button>
          <button
            className={`px-5 py-2 text-sm font-medium rounded-lg border transition-all ${
              subTab === 'params'
                ? 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white shadow-sm'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => { setSubTab('params'); setShowParamsModal(true); }}
            data-testid="qualite-tab-params">
            Paramètres
          </button>
        </div>
      </div>

      {/* Report generation card */}
      <div className="space-y-2" data-testid="report-generation">
        <p className="text-xs text-slate-400 italic">Jamais généré</p>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="text-xs gap-2 border-slate-300"
            data-testid="btn-generate-report">
            <FileText className="h-4 w-4 text-violet-500" />
            <div className="text-left">
              <span className="font-semibold">Rapport qualité</span>
              <br />
              <span className="text-slate-400">Générer le fichier</span>
            </div>
          </Button>
          <button className="text-slate-400 hover:text-slate-600">
            <MoreVertical className="h-4 w-4" />
          </button>
          <button className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center"
            onClick={() => setShowParamsModal(true)} data-testid="btn-open-params">
            <Settings className="h-4 w-4 text-violet-500" />
          </button>
        </div>
      </div>

      {/* Main content: Score + Bar charts */}
      <div className="flex gap-8" data-testid="qualite-main-content">
        {/* Left: Big score + Progression */}
        <div className="w-64 shrink-0 space-y-8">
          {/* Overall score */}
          <div data-testid="overall-score">
            <div className="flex items-baseline gap-1">
              <span className="text-7xl font-black text-violet-600" style={{ lineHeight: 1 }}>
                {overallScore > 0 ? overallScore.toFixed(1) : '—'}
              </span>
              <span className="text-2xl text-slate-400 font-medium">/ 10</span>
            </div>
          </div>

          {/* Before/After spider representation */}
          <div className="space-y-2" data-testid="before-after-scores">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              <span className="font-semibold">{postScore > 0 ? postScore.toFixed(2) : '—'}/10</span> après la formation
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              <span className="font-semibold">{preScore > 0 ? preScore.toFixed(2) : '—'}/10</span> avant la formation
            </p>

            {/* Visual spider/radar-like representation */}
            <div className="mt-4 relative w-48 h-48 mx-auto">
              <svg viewBox="0 0 200 200" className="w-full h-full">
                {/* Grid circles */}
                {[0.2, 0.4, 0.6, 0.8, 1.0].map((r, i) => (
                  <circle key={i} cx="100" cy="100" r={r * 80} fill="none" stroke="#e2e8f0" strokeWidth="0.5" />
                ))}
                {/* Axes */}
                {[0, 60, 120, 180, 240, 300].map((angle, i) => {
                  const rad = (angle * Math.PI) / 180;
                  return (
                    <line key={i} x1="100" y1="100"
                      x2={100 + Math.cos(rad) * 80} y2={100 + Math.sin(rad) * 80}
                      stroke="#e2e8f0" strokeWidth="0.5" />
                  );
                })}
                {/* Pre-formation polygon */}
                {preScore > 0 && (
                  <polygon
                    points={[0, 60, 120, 180, 240, 300].map((angle) => {
                      const rad = (angle * Math.PI) / 180;
                      const r = (preScore / 10) * 80;
                      return `${100 + Math.cos(rad) * r},${100 + Math.sin(rad) * r}`;
                    }).join(' ')}
                    fill="rgba(59,130,246,0.15)" stroke="#3b82f6" strokeWidth="2"
                  />
                )}
                {/* Post-formation polygon */}
                {postScore > 0 && (
                  <polygon
                    points={[0, 60, 120, 180, 240, 300].map((angle) => {
                      const rad = (angle * Math.PI) / 180;
                      const r = (postScore / 10) * 80;
                      return `${100 + Math.cos(rad) * r},${100 + Math.sin(rad) * r}`;
                    }).join(' ')}
                    fill="rgba(139,92,246,0.15)" stroke="#8b5cf6" strokeWidth="2"
                  />
                )}
                {preScore === 0 && postScore === 0 && (
                  <text x="100" y="105" textAnchor="middle" fill="#94a3b8" fontSize="12">Pas de données</text>
                )}
              </svg>
            </div>
          </div>

          {/* Progression */}
          <div data-testid="progression-score">
            <div className="flex items-baseline gap-1">
              <span className={`text-5xl font-black ${progression >= 0 ? 'text-emerald-600' : 'text-red-500'}`} style={{ lineHeight: 1 }}>
                {progression > 0 ? '+' : ''}{progression > 0 ? progression.toFixed(2) : '—'}
              </span>
              <span className="text-xl text-slate-400 font-medium">/ 10</span>
            </div>
            <p className="text-xs text-slate-500 uppercase tracking-wider font-bold mt-2">
              Progression des compétences
            </p>
          </div>
        </div>

        {/* Right: Bar charts */}
        <div className="flex-1 border-l border-slate-200 dark:border-slate-700 pl-8" data-testid="chaud-evaluation">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
              Évaluation à chaud pour les apprenants
            </h4>
            <span className="text-2xl font-bold text-slate-700 dark:text-slate-300">
              {overallScore > 0 ? overallScore.toFixed(1) : '—'}
            </span>
          </div>

          <div className="space-y-5">
            {Object.entries(criteriaScores).map(([label, score]) => (
              <div key={label} className="space-y-1.5" data-testid={`criteria-${label.replace(/\s/g, '-').toLowerCase()}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600 dark:text-slate-300">{label}</span>
                  <span className="text-sm font-semibold text-emerald-600">{score > 0 ? score : '—'}</span>
                </div>
                <div className="h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700 ease-out"
                    style={{
                      width: `${Math.min((score / 10) * 100, 100)}%`,
                      background: 'linear-gradient(90deg, #10b981, #059669)',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {Object.keys(criteriaScores).length === 0 && (
            <div className="py-8 text-center text-sm text-slate-400">
              <Award className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              <p>Aucune évaluation à chaud renseignée.</p>
              <p className="text-xs mt-1">Les résultats apparaîtront lorsque les apprenants auront complété leurs évaluations.</p>
            </div>
          )}
        </div>
      </div>

      {/* Synthèse des résultats */}
      <div className="space-y-3 border-t border-slate-200 dark:border-slate-700 pt-6" data-testid="synthese-section">
        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Synthèse des résultats</h4>
        <textarea
          className="w-full border border-slate-200 dark:border-slate-700 rounded-xl p-4 text-sm bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 resize-y min-h-[100px] focus:ring-2 focus:ring-violet-300 focus:border-violet-400 outline-none"
          placeholder="Rédigez ici la synthèse des résultats de la formation..."
          rows={4}
          value={synthese}
          onChange={e => setSynthese(e.target.value)}
          data-testid="synthese-textarea"
        />
        <Button variant="outline" className="text-xs" onClick={handleSaveSynthese}
          data-testid="btn-save-synthese">
          Enregistrer
        </Button>
      </div>

      {/* Progression sur les objectifs pédagogiques */}
      <div className="space-y-4 border-t border-slate-200 dark:border-slate-700 pt-6" data-testid="objectifs-section">
        <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
          Progression sur les objectifs pédagogiques
        </h4>

        {objectifs.length > 0 ? (
          <div className="space-y-3">
            {objectifs.map((obj, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${i % 3 === 0 ? 'bg-blue-500' : i % 3 === 1 ? 'bg-cyan-500' : 'bg-indigo-500'}`} />
                <p className={`text-sm ${i % 3 === 0 ? 'text-blue-600' : i % 3 === 1 ? 'text-cyan-600' : 'text-indigo-600'}`}>
                  Évaluez vos compétences {obj}
                </p>
              </div>
            ))}

            {/* Progress visualization */}
            <div className="mt-6 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6 relative h-40 overflow-hidden" data-testid="objectifs-chart">
              {objectifs.map((_, i) => {
                const preVal = preScore > 0 ? (preScore * 0.7 + Math.random() * 1.5).toFixed(1) : 0;
                const postVal = postScore > 0 ? (postScore * 0.85 + Math.random() * 1.2).toFixed(1) : 0;
                const yBase = 20 + i * 25;
                return (
                  <div key={i} className="flex items-center gap-3 mb-2">
                    <div className="flex items-center gap-2 w-20">
                      <span className="text-xs font-bold text-blue-600">{preVal > 0 ? preVal : '—'}</span>
                      <span className="text-xs font-bold text-cyan-500">{postVal > 0 ? postVal : '—'}</span>
                    </div>
                    <div className="flex-1 flex gap-1 items-center">
                      <div className="h-4 rounded-full bg-blue-500/70" style={{ width: `${preVal > 0 ? (preVal / 10) * 100 : 5}%`, minWidth: 4 }} />
                      <div className="h-4 rounded-full bg-cyan-400/70" style={{ width: `${postVal > 0 ? (postVal / 10) * 100 : 5}%`, minWidth: 4 }} />
                    </div>
                  </div>
                );
              })}
              {objectifs.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">Aucun objectif pédagogique défini.</p>
              )}
            </div>
          </div>
        ) : (
          <div className="py-6 text-center text-sm text-slate-400">
            <TrendingUp className="h-8 w-8 mx-auto text-slate-300 mb-2" />
            <p>Aucun objectif pédagogique défini pour cette session.</p>
          </div>
        )}
      </div>

      {/* Parameters Modal */}
      {showParamsModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="params-modal">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Sélectionnez les questions visibles dans le rapport qualité
              </h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Anonymize toggle */}
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-3">Anonymiser les réponses</h3>
                <button
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={() => setAnonymize(!anonymize)}
                  data-testid="toggle-anonymize">
                  <div className={`w-12 h-6 rounded-full transition-colors ${anonymize ? 'bg-violet-500' : 'bg-slate-300'} relative`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow absolute top-0.5 transition-transform ${anonymize ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </div>
                  <span className="text-sm text-slate-600 dark:text-slate-300">Anonymiser les réponses</span>
                </button>
              </div>

              {/* Progression checkbox */}
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-3">
                  Progression sur les objectifs pédagogiques
                </h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={showProgressionCurve}
                    onChange={e => setShowProgressionCurve(e.target.checked)}
                    className="w-5 h-5 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                    data-testid="checkbox-progression" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">Courbe de progression</span>
                </label>
              </div>

              {/* Preformation questions */}
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-3">
                  Questionnaire préformation avec enquête pour l'inclusivité
                </h3>
                <button
                  className="px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 mb-4"
                  onClick={toggleAllPreformation}
                  data-testid="btn-toggle-all-preformation">
                  Tout cocher / décocher
                </button>
                <div className="space-y-1">
                  {ALL_PREFORMATION_QUESTIONS.map((q, i) => (
                    <label key={i}
                      className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-colors ${
                        i % 2 === 1 ? 'bg-slate-50 dark:bg-slate-700/30' : 'bg-white dark:bg-slate-800'
                      }`}
                      data-testid={`preformation-q-${i}`}>
                      <input type="checkbox" checked={visiblePreformation.includes(q)}
                        onChange={() => togglePreformationQ(q)}
                        className="w-5 h-5 mt-0.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500 shrink-0" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{q}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Évaluation à chaud questions */}
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-3">
                  Évaluation à chaud de la formation que vous venez de suivre
                </h3>
                <button
                  className="px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 mb-4"
                  onClick={toggleAllChaud}
                  data-testid="btn-toggle-all-chaud">
                  Tout cocher / décocher
                </button>
                <div className="space-y-1">
                  {ALL_CHAUD_QUESTIONS.map((q, i) => (
                    <label key={i}
                      className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-colors ${
                        i % 2 === 1 ? 'bg-slate-50 dark:bg-slate-700/30' : 'bg-white dark:bg-slate-800'
                      }`}
                      data-testid={`chaud-q-${i}`}>
                      <input type="checkbox" checked={visibleChaud.includes(q)}
                        onChange={() => toggleChaudQ(q)}
                        className="w-5 h-5 mt-0.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500 shrink-0" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{q}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal footer */}
            <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex items-center gap-3">
              <Button className="bg-violet-600 hover:bg-violet-700 text-white text-sm px-6"
                onClick={handleSaveConfig} data-testid="btn-save-config">
                Enregistrer
              </Button>
              <Button variant="outline" className="text-sm"
                onClick={() => setShowParamsModal(false)} data-testid="btn-cancel-config">
                Annuler
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuiviQualite;
